// src/pages/Home.js
import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ShieldCheck, Truck, RefreshCw, Gem, Clock, Instagram } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, Navigation, EffectFade } from 'swiper/modules';

import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import 'swiper/css/effect-fade';

const sliderImages = [
  '/images/slider/slider-1.jpg',
  '/images/slider/slider-2.jpg',
  '/images/slider/slider-3.jpg',
  '/images/slider/slider-4.jpg',
  '/images/slider/slider-5.jpg',
  '/images/slider/slider-6.jpg',
  '/images/slider/slider-7.jpg',
  '/images/slider/slider-8.jpg',
  '/images/slider/slider-9.jpg',
  '/images/slider/slider-10.jpg',
  '/images/slider/slider-11.jpg',
];

// انیمیشن‌های متن
const textVariants = {
  hidden: { opacity: 0, y: 100 },
  visible: { opacity: 1, y: 0, transition: { duration: 1.2, ease: "easeOut" } }
};

const buttonVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.8, delay: 0.6, type: "spring", stiffness: 120 } },
  hover: { scale: 1.08, boxShadow: "0 20px 40px rgba(212, 175, 55, 0.4)" }
};

const Home = () => {
  return (
    <div className="flex flex-col min-h-screen bg-white">
      {/* ۱. هیرو سکشن با اسلایدر حرفه‌ای */}
      <section className="relative h-[70vh] md:h-[85vh] lg:h-[90vh] flex-shrink-0 overflow-hidden">
        <Swiper
          modules={[Autoplay, Pagination, Navigation, EffectFade]}
          spaceBetween={0}
          slidesPerView={1}
          effect="fade"
          fadeEffect={{ crossFade: true }}
          autoplay={{
            delay: 7000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }}
          pagination={{
            clickable: true,
            bulletClass: 'swiper-custom-bullet',
            bulletActiveClass: 'swiper-custom-bullet-active',
          }}
          navigation={{
            nextEl: '.swiper-button-next-custom',
            prevEl: '.swiper-button-prev-custom',
          }}
          loop={true}
          speed={1200}
          className="h-full w-full"
        >
          {sliderImages.map((img, index) => (
            <SwiperSlide key={index}>
              <div className="relative h-full w-full group">
                {/* عکس با Ken Burns + parallax ساده */}
                <div className="absolute inset-0 w-[110%] h-[110%] -left-[5%] -top-[5%] transition-transform duration-300 ease-out group-hover:scale-110">
                  <img
                    src={img}
                    alt={`اسلاید ${index + 1}`}
                    className="w-full h-full object-cover object-center transition-transform duration-[15000ms] ease-out group-hover:scale-115"
                    loading={index < 3 ? 'eager' : 'lazy'}
                  />
                </div>

                {/* گرادیان لوکس */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/40 to-transparent/20"></div>

                {/* متن با انیمیشن پیشرفته */}
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-white z-10 px-6">
                  <motion.h1
                    variants={textVariants}
                    initial="hidden"
                    animate="visible"
                    className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold mb-6 drop-shadow-2xl"
                  >
                    گالری طلا و جواهری خرمی
                  </motion.h1>

                  <motion.p
                    variants={textVariants}
                    initial="hidden"
                    animate="visible"
                    transition={{ ...textVariants.visible.transition, delay: 0.4 }}
                    className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-medium mb-12 drop-shadow-lg max-w-4xl"
                  >
                    از سال ۱۳۵۰ – اصالت و زیبایی نسل به نسل
                  </motion.p>

                  <motion.div
                    className="flex flex-col sm:flex-row gap-6"
                    variants={buttonVariants}
                    initial="hidden"
                    animate="visible"
                    whileHover="hover"
                  >
                    <Link
                      to="/products"
                      className="group px-8 py-4 md:px-12 md:py-5 bg-gold hover:bg-amber-600 text-white text-lg md:text-xl font-bold rounded-full transition-all shadow-2xl flex items-center gap-3"
                    >
                      مشاهده محصولات
                      <ArrowRight size={24} className="transition-transform group-hover:translate-x-3" />
                    </Link>

                    <Link
                      to="/about"
                      className="px-8 py-4 md:px-12 md:py-5 bg-transparent border-2 border-gold text-gold hover:bg-gold hover:text-white text-lg md:text-xl font-bold rounded-full transition-all"
                    >
                      درباره ما
                    </Link>
                  </motion.div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        {/* فلش‌های سفارشی */}
        <div className="swiper-button-prev-custom absolute left-6 md:left-20 top-1/2 -translate-y-1/2 z-30 text-white text-5xl md:text-6xl cursor-pointer hover:text-gold transition-all duration-500 opacity-70 hover:opacity-100 transform hover:scale-110">
          ←
        </div>
        <div className="swiper-button-next-custom absolute right-6 md:right-20 top-1/2 -translate-y-1/2 z-30 text-white text-5xl md:text-6xl cursor-pointer hover:text-gold transition-all duration-500 opacity-70 hover:opacity-100 transform hover:scale-110">
          →
        </div>

        {/* نقطه‌های سفارشی */}
        <style jsx global>{`
          .swiper-pagination-bullet {
            width: 16px !important;
            height: 16px !important;
            background: rgba(255, 255, 255, 0.6) !important;
            opacity: 1 !important;
            transition: all 0.4s ease !important;
            margin: 0 8px !important;
          }
          .swiper-pagination-bullet-active {
            background: #d4af37 !important;
            transform: scale(1.5) !important;
            box-shadow: 0 0 20px rgba(212, 175, 55, 0.8) !important;
          }
        `}</style>
      </section>

      {/* ۲. محتوای میانی - مزایا */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-bold text-center text-gray-800 mb-16"
          >
            چرا مشتریان ما را انتخاب می‌کنند؟
          </motion.h2>

          <div className="grid md:grid-cols-3 gap-10">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white p-10 rounded-3xl shadow-xl hover:shadow-2xl transition text-center"
            >
              <Gem size={64} className="mx-auto mb-6 text-gold" />
              <h3 className="text-2xl font-bold mb-4">کیفیت و اصالت تضمینی</h3>
              <p className="text-gray-700">
                تمام محصولات دارای فاکتور رسمی، شناسنامه اصالت و ضمانت بازگشت ۷ روزه
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white p-10 rounded-3xl shadow-xl hover:shadow-2xl transition text-center"
            >
              <Clock size={64} className="mx-auto mb-6 text-gold" />
              <h3 className="text-2xl font-bold mb-4">تجربه ۷۵ ساله</h3>
              <p className="text-gray-700">
                از سال ۱۳۵۰ تاکنون، اعتماد نسل‌ها را با خود همراه داریم
              </p>
            </motion.div>

         
          </div>
        </div>
      </section>

      {/* ۳. کال‌تواکشن بزرگ */}
      <section className="py-20 bg-gradient-to-r from-gold to-amber-700 text-white text-center">
        <div className="container mx-auto px-6">
          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-bold mb-8"
          >
            آماده درخشیدن هستید؟
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-xl md:text-2xl mb-12 max-w-3xl mx-auto"
          >
            جدیدترین مجموعه‌های طلا و جواهر را همین حالا کشف کنید
          </motion.p>

          <motion.div
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.98 }}
          >
            <Link
              to="/products"
              className="inline-block px-12 py-6 bg-white text-gold-dark text-2xl font-bold rounded-full hover:bg-gray-100 transition shadow-2xl"
            >
              مشاهده مجموعه‌ها
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ۴. فوتر (بدون فاصله اضافی بالا) */}
      <footer className="bg-gradient-to-b from-gray-900 to-black text-white pt-12 pb-8 mt-0">
        {/* محتوای فوتر قبلی رو اینجا بگذار */}
      </footer>
    </div>
  );
};

export default Home;